import { useState, useEffect } from "react";
import { MapPin, ArrowSquareOut, Warning, CaretUp, CaretDown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const HOME_KEY = "gresart_home_address";
const HOTEL_KEY = "gresart_hotel_address";

// A start/end point picker: "Primeira/última visita" (default, no override), a saved
// "Minha Casa" / "Hotel" address (persisted in localStorage, reusable across days), or a
// one-off custom address.
function PointSelector({ label, mode, setMode, customValue, setCustomValue, savedValue, setSavedValue, defaultLabel, testIdPrefix }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-stone-500">{label}</Label>
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger data-testid={`${testIdPrefix}-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="default">{defaultLabel}</SelectItem>
          <SelectItem value="home">Minha Casa</SelectItem>
          <SelectItem value="hotel">Hotel</SelectItem>
          <SelectItem value="custom">Outro endereço...</SelectItem>
        </SelectContent>
      </Select>
      {mode === "custom" && (
        <Input
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          placeholder="Endereço completo"
          data-testid={`${testIdPrefix}-custom-input`}
        />
      )}
      {(mode === "home" || mode === "hotel") && (
        <Input
          value={savedValue}
          onChange={(e) => {
            setSavedValue(e.target.value);
            localStorage.setItem(mode === "home" ? HOME_KEY : HOTEL_KEY, e.target.value);
          }}
          placeholder={mode === "home" ? "Endereço da sua casa" : "Endereço do hotel"}
          data-testid={`${testIdPrefix}-${mode}-input`}
        />
      )}
    </div>
  );
}

// Builds the day's route from scheduled tasks that have a known client/gabinete address.
// The user can reorder the stops and optionally override the start/end point (e.g. home
// or hotel) - default behaviour (no overrides) is the plain scheduled-order route.
export function RouteMapModal({ open, onClose, dayLabel, tasks = [] }) {
  const [orderedStops, setOrderedStops] = useState([]);
  const [startMode, setStartMode] = useState("default");
  const [startCustom, setStartCustom] = useState("");
  const [homeAddress, setHomeAddress] = useState(() => localStorage.getItem(HOME_KEY) || "");
  const [endMode, setEndMode] = useState("default");
  const [endCustom, setEndCustom] = useState("");
  const [hotelAddress, setHotelAddress] = useState(() => localStorage.getItem(HOTEL_KEY) || "");

  const skippedCount = tasks.length - tasks.filter((t) => t.entity_address && t.entity_address.trim()).length;

  useEffect(() => {
    if (!open) return;
    const withAddress = tasks.filter((t) => t.entity_address && t.entity_address.trim());
    // De-duplicate consecutive stops at the same address (e.g. two visits to the same
    // client on one day) - otherwise Google Maps gets origin === destination.
    setOrderedStops(withAddress.filter((t, i) => i === 0 || t.entity_address !== withAddress[i - 1].entity_address));
    setStartMode("default");
    setStartCustom("");
    setEndMode("default");
    setEndCustom("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // only re-initialize when the modal is opened, not on every parent re-render
             // (e.g. a week refetch) while it's already open, so manual reordering isn't lost

  const moveStop = (index, direction) => {
    setOrderedStops((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const resolvePoint = (mode, custom) => {
    if (mode === "home") return homeAddress.trim();
    if (mode === "hotel") return hotelAddress.trim();
    if (mode === "custom") return custom.trim();
    return null;
  };

  const startAddress = resolvePoint(startMode, startCustom);
  const endAddress = resolvePoint(endMode, endCustom);
  const startPointObj = startAddress ? { entity_name: startMode === "home" ? "Minha Casa" : startMode === "hotel" ? "Hotel" : "Partida", entity_address: startAddress } : null;
  const endPointObj = endAddress ? { entity_name: endMode === "home" ? "Minha Casa" : endMode === "hotel" ? "Hotel" : "Chegada", entity_address: endAddress } : null;

  const rawRoute = [...(startPointObj ? [startPointObj] : []), ...orderedStops, ...(endPointObj ? [endPointObj] : [])];
  const route = rawRoute.filter((p, i) => i === 0 || p.entity_address !== rawRoute[i - 1].entity_address);

  const buildNativeMapsUrl = () => {
    if (route.length === 0) return null;
    if (route.length === 1) {
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(route[0].entity_address)}&travelmode=driving`;
    }
    const destination = encodeURIComponent(route[route.length - 1].entity_address);
    const waypoints = route.slice(0, -1).map((s) => encodeURIComponent(s.entity_address)).join("|");
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
  };

  const buildEmbedUrl = () => {
    if (!GOOGLE_MAPS_API_KEY || route.length === 0) return null;
    if (route.length === 1) {
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(route[0].entity_address)}`;
    }
    const origin = encodeURIComponent(route[0].entity_address);
    const destination = encodeURIComponent(route[route.length - 1].entity_address);
    const waypoints = route.slice(1, -1).map((s) => encodeURIComponent(s.entity_address)).join("|");
    return `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ""}&mode=driving`;
  };

  const nativeUrl = buildNativeMapsUrl();
  const embedUrl = buildEmbedUrl();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="route-map-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={20} className="text-[#009392]" /> Rota do dia - {dayLabel}
          </DialogTitle>
          <DialogDescription className="sr-only">Detalhes da rota do dia</DialogDescription>
        </DialogHeader>

        {orderedStops.length === 0 && !startAddress && !endAddress ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-stone-500" data-testid="route-map-empty">
            <Warning size={28} className="text-amber-400" />
            <p className="text-sm">Nenhuma tarefa deste dia tem cliente/gabinete com endereço registado.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PointSelector
                label="Ponto de partida"
                mode={startMode} setMode={setStartMode}
                customValue={startCustom} setCustomValue={setStartCustom}
                savedValue={homeAddress} setSavedValue={setHomeAddress}
                defaultLabel="Primeira visita"
                testIdPrefix="route-start"
              />
              <PointSelector
                label="Ponto de chegada"
                mode={endMode} setMode={setEndMode}
                customValue={endCustom} setCustomValue={setEndCustom}
                savedValue={hotelAddress} setSavedValue={setHotelAddress}
                defaultLabel="Última visita"
                testIdPrefix="route-end"
              />
            </div>

            {(orderedStops.length > 0 || startPointObj || endPointObj) && (
              <div className="space-y-1.5" data-testid="route-stops-list">
                <Label className="text-xs text-stone-500">Ordem da rota (reordene as paragens com as setas)</Label>
                {startPointObj && (
                  <div className="flex items-center gap-2 bg-[#009392]/5 border border-[#009392]/20 rounded-lg px-3 py-2" data-testid="route-point-start">
                    <span className="text-xs font-semibold text-[#009392] w-4">A</span>
                    <div className="flex-1 min-w-0 text-sm">
                      <span className="font-medium text-stone-800">{startPointObj.entity_name}</span>
                      <span className="text-stone-500"> - {startPointObj.entity_address}</span>
                      {startPointObj.entity_address === orderedStops[0]?.entity_address && (
                        <span className="text-amber-600"> (igual à 1ª parada - não duplicado na rota)</span>
                      )}
                    </div>
                  </div>
                )}
                {orderedStops.map((s, i) => (
                  <div key={s.id || i} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" data-testid={`route-stop-${i}`}>
                    <span className="text-xs font-semibold text-stone-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0 text-sm">
                      <span className="font-medium text-stone-800">{s.entity_name}</span>
                      <span className="text-stone-500"> - {s.entity_address}</span>
                      {s.scheduled_time && <span className="text-stone-500"> ({s.scheduled_time})</span>}
                    </div>
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => moveStop(i, -1)}
                        disabled={i === 0}
                        className="p-0.5 text-stone-400 hover:text-[#009392] disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`btn-move-stop-up-${i}`}
                      >
                        <CaretUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStop(i, 1)}
                        disabled={i === orderedStops.length - 1}
                        className="p-0.5 text-stone-400 hover:text-[#009392] disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`btn-move-stop-down-${i}`}
                      >
                        <CaretDown size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {endPointObj && (
                  <div className="flex items-center gap-2 bg-[#009392]/5 border border-[#009392]/20 rounded-lg px-3 py-2" data-testid="route-point-end">
                    <span className="text-xs font-semibold text-[#009392] w-4">B</span>
                    <div className="flex-1 min-w-0 text-sm">
                      <span className="font-medium text-stone-800">{endPointObj.entity_name}</span>
                      <span className="text-stone-500"> - {endPointObj.entity_address}</span>
                      {endPointObj.entity_address === orderedStops[orderedStops.length - 1]?.entity_address && (
                        <span className="text-amber-600"> (igual à última parada - não duplicado na rota)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {embedUrl ? (
              <iframe
                title="Rota do dia"
                src={embedUrl}
                className="w-full h-72 rounded-xl border border-stone-200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="route-map-iframe"
              />
            ) : (
              <div className="w-full h-40 rounded-xl border border-stone-200 flex items-center justify-center text-sm text-stone-400 bg-stone-50">
                Pré-visualização do mapa não disponível
              </div>
            )}

            {skippedCount > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1.5" data-testid="route-skipped-note">
                <Warning size={13} /> {skippedCount} tarefa(s) sem endereço registado {skippedCount === 1 ? "foi" : "foram"} ignorada(s) na rota.
              </p>
            )}

            <Button
              className="w-full btn-primary"
              onClick={() => window.open(nativeUrl, "_blank")}
              disabled={!nativeUrl}
              data-testid="btn-open-google-maps"
            >
              <ArrowSquareOut size={16} className="mr-1.5" /> Abrir no Google Maps
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
